import { Button } from '@/components/button';
import { TextInput } from '@/components/inputs/text-input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/contexts/i18n-context';
import { zt } from '@/util/validators';
import { useNavigate } from '@/util/react-router-dom';
import { useRestoreUsernames } from '@/api/username-restoration';
import { UserEntryFormContainer } from '@/components/auth/user-entry-form-container';
import { UserEntryLayout } from '@/components/auth/user-entry-layout';
import { useBranding } from '@/outlets/branding-outlet';

const schema = z.object({
  email: z.string().email({ message: zt('common.errors.email') }),
});

type FormValues = z.infer<typeof schema>;

export function UsernameRestorationPage() {
  const { t } = useTranslation();
  const { networkId } = useBranding();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  const { mutate: restoreUsernames } = useRestoreUsernames();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    restoreUsernames(
      { data: { email: data.email, networkId } },
      {
        onSuccess: () => navigate(`/restore-usernames/success?email=${data.email}`),
      },
    );
  };

  return (
    <UserEntryLayout>
      <UserEntryFormContainer
        title={t('usernameRestoration.title')}
        description={t('usernameRestoration.description')}
        form={form}
        onSubmit={onSubmit}
      >
        <TextInput
          name="email"
          label={t('usernameRestoration.label')}
          placeholder={t('usernameRestoration.placeholder')}
        />
        <Button fullWidth type="submit">
          {t('usernameRestoration.submit')}
        </Button>
      </UserEntryFormContainer>
    </UserEntryLayout>
  );
}
